import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, ClipboardCheck, Mail, Plus, UserX, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ErrorState, LoadingState, PageHeader } from "@/components/common/States";
import { GuidePanel } from "@/components/common/GuidePanel";
import { fetcher } from "@/lib/api";
import { ACTION_LABELS, TENANT_STATUS, formatDate } from "@/lib/labels";

const AttentionList = ({ title, items, empty, testId }) => (
  <section className="rounded-2xl border bg-card p-6" data-testid={testId}>
    <h2 className="text-base md:text-lg font-bold">{title}</h2>
    {!items.length ? <p className="text-sm text-muted-foreground mt-3">{empty}</p> : (
      <ul className="mt-3 divide-y">
        {items.map((s) => (
          <li key={s.id}>
            <Link to={`/pusat-kendali/sekolah/${s.id}`} className="flex items-center justify-between gap-3 py-2.5 hover:text-[#3B82F6]" data-testid={`${testId}-${s.id}`}>
              <span className="text-sm font-medium">{s.name}<span className="block text-xs text-muted-foreground">{s.city}</span></span>
              <span className="text-xs font-semibold text-[#3B82F6]">Buka</span>
            </Link>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="rounded-2xl border bg-card p-5" data-testid={testId}>
    <Icon className={`h-5 w-5 ${accent || "text-[#3B82F6]"}`} aria-hidden />
    <p className="font-brand text-3xl font-extrabold mt-3">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export default function PlatformSummary() {
  const q = useQuery({ queryKey: ["platform-summary"], queryFn: () => fetcher("/platform/summary") });
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const s = q.data;
  const steps = [
    { key: "tenant", label: "Buat tenant sekolah pertama", done: s.schools_total > 0, to: "/pusat-kendali/sekolah/baru" },
    { key: "admin", label: `Undang Admin Sekolah (${s.schools_without_admin} sekolah belum punya admin)`, done: s.schools_without_admin === 0, to: "/pusat-kendali/sekolah", hint: "Buka detail sekolah lalu buat tautan undangan." },
    { key: "verify", label: `Verifikasi sekolah yang menunggu (${s.schools_by_status.Pending_Verification})`, done: s.schools_by_status.Pending_Verification === 0, to: "/pusat-kendali/sekolah?status=Pending_Verification" },
    { key: "review", label: `Selesaikan tinjauan identitas (${s.open_identity_reviews})`, done: s.open_identity_reviews === 0, to: "/pusat-kendali/tinjauan-identitas" },
  ];
  return (
    <div className="space-y-10">
      <PageHeader eyebrow="Pusat Kendali SIST" title="Ringkasan Platform" description="Gambaran metadata seluruh tenant. Data akademik sekolah tidak ditampilkan di sini."
        actions={<Button asChild data-testid="summary-create-school-button"><Link to="/pusat-kendali/sekolah/baru"><Plus className="h-4 w-4 mr-1" /> Buat Sekolah</Link></Button>} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat icon={Building2} label="Total sekolah" value={s.schools_total} testId="stat-schools-total" />
        {Object.entries(TENANT_STATUS).map(([k, v]) => (
          <Stat key={k} icon={Building2} label={v.label} value={s.schools_by_status[k]} testId={`stat-status-${k}`} accent={k === "Active" ? "text-emerald-600" : k === "Restricted" ? "text-[#E11D2E]" : "text-slate-500"} />
        ))}
        <Stat icon={Users} label="Akun pengguna sekolah" value={s.school_users_total} testId="stat-users" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Stat icon={Mail} label="Undangan menunggu" value={s.pending_invitations} testId="stat-pending-invitations" />
        <Stat icon={ClipboardCheck} label="Tinjauan identitas terbuka" value={s.open_identity_reviews} testId="stat-open-reviews" accent="text-amber-600" />
        <Stat icon={UserX} label="Sekolah tanpa Admin Sekolah" value={s.schools_without_admin} testId="stat-no-admin" accent="text-[#E11D2E]" />
      </div>
      <GuidePanel storageKey="sist-guide-platform" title="Misi pengelolaan platform" steps={steps} />
      <div className="grid lg:grid-cols-2 gap-6">
        <AttentionList title="Menunggu verifikasi" items={s.attention.pending_verification} empty="Tidak ada sekolah yang menunggu verifikasi." testId="attention-pending" />
        <AttentionList title="Belum punya Admin Sekolah" items={s.attention.without_admin} empty="Semua sekolah aktif sudah memiliki Admin Sekolah." testId="attention-no-admin" />
      </div>
      <section className="rounded-2xl border bg-card p-6" data-testid="platform-recent-activity">
        <div className="flex items-center justify-between">
          <h2 className="text-base md:text-lg font-bold">Aktivitas platform terbaru</h2>
          <Link to="/pusat-kendali/audit" className="text-sm font-semibold text-[#3B82F6] hover:underline" data-testid="platform-activity-all-link">Lihat log audit</Link>
        </div>
        {!s.recent_activity.length ? <p className="text-sm text-muted-foreground mt-3">Belum ada aktivitas.</p> : (
          <ul className="mt-3 divide-y">
            {s.recent_activity.map((a) => (
              <li key={a.id} className="py-2.5 text-sm">
                <p><span className="font-medium">{ACTION_LABELS[a.action] || a.action}</span>{a.target_label && <> · {a.target_label}</>}{a.reason && <span className="text-muted-foreground"> — {a.reason}</span>}</p>
                <p className="text-xs text-muted-foreground">{formatDate(a.at)} · {a.actor_email || "Sistem"}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
